import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, Users, Filter, Download, AlertTriangle, Building2, ClipboardList, Check, X, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isToday, isSameDay, parseISO, isWithinInterval } from "date-fns";
import { es } from "date-fns/locale";
import { getWorkTypeColor, getWorkTypeLabel, tipoTrabajoLabels } from "@/lib/workTypeConstants";
import { cn } from "@/lib/utils";
import { useAusencias, TIPO_AUSENCIA_LABEL, TIPO_AUSENCIA_COLOR } from "@/contexts/AusenciasContext";
import { useObrasContext } from "@/contexts/ObrasContext";
import { Link } from "react-router-dom";
import { JornadaDetalleCardDialog } from "@/components/jornadas/JornadaDetalleCardDialog";
import { JornadaForm } from "@/components/jornadas/JornadaForm";

interface Jornada {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string | null;
  horas_trabajadas: number | null;
  tipo_trabajo: string;
  descripcion_trabajo: string | null;
  es_hora_extra: boolean;
  tecnico_id: string;
  obra_id: string | null;
  orden_trabajo_id: string | null;
  cliente_id: string | null;
  estado_validacion: 'pendiente' | 'aprobada' | 'rechazada';
  motivo_rechazo: string | null;
  tecnico?: { nombre: string; email: string } | null;
  obra?: { nombre: string } | null;
  orden_trabajo?: { numero_ot: string; titulo: string } | null;
  cliente?: { nombre: string } | null;
}

interface Tecnico {
  user_id: string;
  nombre: string;
  email: string;
}

interface TecnicoStats {
  tecnico_id: string;
  nombre: string;
  horas_totales: number;
  horas_extra: number;
  dias_trabajados: number;
  jornadas_count: number;
}

const JornadasEquipo: React.FC = () => {
  const { perfil } = useAuth();
  const { ausencias: allAusencias } = useAusencias();
  const { obras, deleteJornada } = useObrasContext();
  const obrasActivas = useMemo(() => obras.filter((o) => o.estado === 'activa'), [obras]);
  const [jornadas, setJornadas] = useState<Jornada[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedTecnico, setSelectedTecnico] = useState<string>("all");
  const [fechaDesde, setFechaDesde] = useState<string>(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [fechaHasta, setFechaHasta] = useState<string>(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [tipoTrabajoFilter, setTipoTrabajoFilter] = useState<string>("all");
  const [soloExtras, setSoloExtras] = useState(false);
  const [estadoValidacionFilter, setEstadoValidacionFilter] = useState<string>("pendiente");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarView, setCalendarView] = useState<'mes' | 'semana'>('mes');
  const [rechazoDialogOpen, setRechazoDialogOpen] = useState(false);
  const [jornadaARechazar, setJornadaARechazar] = useState<Jornada | null>(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [accionLoading, setAccionLoading] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [jornadasDetalle, setJornadasDetalle] = useState<Jornada[]>([]);
  const [editarJornadaOpen, setEditarJornadaOpen] = useState(false);
  const [jornadaEnEdicion, setJornadaEnEdicion] = useState<Jornada | null>(null);

  const abrirDetalleJornadas = (items: Jornada[]) => {
    if (!items.length) return;
    setJornadasDetalle(items);
    setDetalleOpen(true);
  };

  const handleRechazarDesdeDetalle = async (j: Jornada, motivo: string) => {
    if (!perfil?.user_id) return;
    setAccionLoading(true);
    const { error } = await supabase
      .from('obra_jornadas')
      .update({
        estado_validacion: 'rechazada',
        motivo_rechazo: motivo,
        revisada_por: perfil.user_id,
        fecha_revision: new Date().toISOString(),
      })
      .eq('id', j.id);
    setAccionLoading(false);
    if (error) {
      toast({ title: "Error al rechazar", description: error.message, variant: "destructive" });
      return;
    }
    setJornadas(prev => prev.map(x => x.id === j.id ? { ...x, estado_validacion: 'rechazada', motivo_rechazo: motivo } : x));
    setJornadasDetalle(prev => prev.map(x => x.id === j.id ? { ...x, estado_validacion: 'rechazada', motivo_rechazo: motivo } : x));
    toast({ title: "Jornada rechazada" });
  };

  const aprobarDesdeDetalle = async (j: Jornada) => {
    await aprobarJornada(j);
    setJornadasDetalle(prev => prev.map(x => x.id === j.id ? { ...x, estado_validacion: 'aprobada', motivo_rechazo: null } : x));
  };

  const reabrirJornada = async (j: Jornada) => {
    if (!perfil?.user_id) return;
    setAccionLoading(true);
    const { error } = await supabase
      .from('obra_jornadas')
      .update({
        estado_validacion: 'pendiente',
        motivo_rechazo: null,
        revisada_por: perfil.user_id,
        fecha_revision: new Date().toISOString(),
      })
      .eq('id', j.id);
    setAccionLoading(false);
    if (error) {
      toast({ title: "Error al reabrir", description: error.message, variant: "destructive" });
      return;
    }
    setJornadas(prev => prev.map(x => x.id === j.id ? { ...x, estado_validacion: 'pendiente', motivo_rechazo: null } : x));
    setJornadasDetalle(prev => prev.map(x => x.id === j.id ? { ...x, estado_validacion: 'pendiente', motivo_rechazo: null } : x));
    toast({ title: "Jornada reabierta", description: "Recuerda volver a aprobarla tras corregirla." });
  };

  const abrirEditorJornada = (j: Jornada) => {
    setJornadaEnEdicion(j);
    setEditarJornadaOpen(true);
    setDetalleOpen(false);
  };

  const recargarTrasEdicion = async () => {
    // Recargar la jornada editada desde DB para reflejar nuevos valores (horas, coste, etc.)
    if (!jornadaEnEdicion) return;
    const { data } = await supabase
      .from('obra_jornadas')
      .select(`
        *,
        tecnico:perfiles!fk_obra_jornadas_tecnico_id(nombre, email),
        obra:obras!fk_obra_jornadas_obra_id(nombre),
        orden_trabajo:ordenes_trabajo(numero_ot, titulo),
        cliente:clientes(nombre)
      `)
      .eq('id', jornadaEnEdicion.id)
      .maybeSingle();
    if (data) {
      setJornadas(prev => prev.map(x => x.id === data.id ? (data as unknown as Jornada) : x));
    }
    setEditarJornadaOpen(false);
    setJornadaEnEdicion(null);
  };

  const isOfficeRole = perfil?.rol === 'admin' || perfil?.rol === 'gestor';

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!perfil?.empresa_id) return;
      
      setLoading(true);
      try {
        // ALL members of the empresa (any role/status) — used to scope jornadas to empresa
        const { data: miembrosData } = await supabase
          .from('perfiles')
          .select('user_id')
          .eq('empresa_id', perfil.empresa_id);

        // Active office/field roles — used to populate the technician dropdown & cards
        const { data: tecnicosData } = await supabase
          .from('perfiles')
          .select('user_id, nombre, email')
          .eq('empresa_id', perfil.empresa_id)
          .in('rol', ['tecnico', 'admin', 'gestor'])
          .eq('estado', 'activo');

        if (tecnicosData) {
          setTecnicos(tecnicosData);
        }

        // Load jornadas with relations
        const { data: jornadasData } = await supabase
          .from('obra_jornadas')
          .select(`
            *,
            tecnico:perfiles!fk_obra_jornadas_tecnico_id(nombre, email),
            obra:obras!fk_obra_jornadas_obra_id(nombre),
            orden_trabajo:ordenes_trabajo(numero_ot, titulo),
            cliente:clientes(nombre)
          `)
          .gte('fecha', fechaDesde)
          .lte('fecha', fechaHasta)
          .order('fecha', { ascending: false })
          .order('hora_inicio', { ascending: false });

        if (jornadasData) {
          // Scope by empresa membership (includes inactive users & gestores)
          const empresaMemberIds = new Set((miembrosData || []).map(m => m.user_id));
          const filteredJornadas = jornadasData.filter(j => empresaMemberIds.has(j.tecnico_id));
          setJornadas(filteredJornadas as unknown as Jornada[]);
        }
      } catch (error) {
        console.error('Error loading jornadas:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [perfil?.empresa_id, fechaDesde, fechaHasta]);

  // Jornadas filtered by everything EXCEPT estado_validacion (for counters)
  const jornadasSinFiltroEstado = useMemo(() => {
    return jornadas.filter(jornada => {
      if (selectedTecnico !== "all" && jornada.tecnico_id !== selectedTecnico) return false;
      if (tipoTrabajoFilter !== "all" && jornada.tipo_trabajo !== tipoTrabajoFilter) return false;
      if (soloExtras && !jornada.es_hora_extra) return false;
      return true;
    });
  }, [jornadas, selectedTecnico, tipoTrabajoFilter, soloExtras]);

  const conteosEstado = useMemo(() => ({
    all: jornadasSinFiltroEstado.length,
    pendiente: jornadasSinFiltroEstado.filter(j => j.estado_validacion === 'pendiente').length,
    aprobada: jornadasSinFiltroEstado.filter(j => j.estado_validacion === 'aprobada').length,
    rechazada: jornadasSinFiltroEstado.filter(j => j.estado_validacion === 'rechazada').length,
  }), [jornadasSinFiltroEstado]);

  // Filtered jornadas
  const filteredJornadas = useMemo(() => {
    return jornadasSinFiltroEstado.filter(jornada => {
      if (estadoValidacionFilter !== "all" && jornada.estado_validacion !== estadoValidacionFilter) return false;
      return true;
    });
  }, [jornadasSinFiltroEstado, estadoValidacionFilter]);

  const aprobarJornada = async (jornada: Jornada) => {
    if (!perfil?.user_id) return;
    setAccionLoading(true);
    const { error } = await supabase
      .from('obra_jornadas')
      .update({
        estado_validacion: 'aprobada',
        motivo_rechazo: null,
        revisada_por: perfil.user_id,
        fecha_revision: new Date().toISOString(),
      })
      .eq('id', jornada.id);
    setAccionLoading(false);
    if (error) {
      toast({ title: "Error al aprobar", description: error.message, variant: "destructive" });
      return;
    }
    setJornadas(prev => prev.map(j => j.id === jornada.id ? { ...j, estado_validacion: 'aprobada', motivo_rechazo: null } : j));
    toast({ title: "Jornada aprobada" });
  };

  const confirmarRechazo = async () => {
    if (!jornadaARechazar || !perfil?.user_id) return;
    if (!motivoRechazo.trim()) {
      toast({ title: "Indica un motivo", variant: "destructive" });
      return;
    }
    setAccionLoading(true);
    const { error } = await supabase
      .from('obra_jornadas')
      .update({
        estado_validacion: 'rechazada',
        motivo_rechazo: motivoRechazo.trim(),
        revisada_por: perfil.user_id,
        fecha_revision: new Date().toISOString(),
      })
      .eq('id', jornadaARechazar.id);
    setAccionLoading(false);
    if (error) {
      toast({ title: "Error al rechazar", description: error.message, variant: "destructive" });
      return;
    }
    setJornadas(prev => prev.map(j => j.id === jornadaARechazar.id ? { ...j, estado_validacion: 'rechazada', motivo_rechazo: motivoRechazo.trim() } : j));
    toast({ title: "Jornada rechazada", description: "El técnico la verá marcada para corregir." });
    setRechazoDialogOpen(false);
    setJornadaARechazar(null);
    setMotivoRechazo("");
  };

  // Stats per technician
  const tecnicoStats = useMemo((): TecnicoStats[] => {
    const statsMap = new Map<string, TecnicoStats>();
    
    filteredJornadas.forEach(jornada => {
      const tecnicoId = jornada.tecnico_id;
      const tecnicoNombre = jornada.tecnico?.nombre || 'Desconocido';
      
      if (!statsMap.has(tecnicoId)) {
        statsMap.set(tecnicoId, {
          tecnico_id: tecnicoId,
          nombre: tecnicoNombre,
          horas_totales: 0,
          horas_extra: 0,
          dias_trabajados: 0,
          jornadas_count: 0,
        });
      }
      
      const stats = statsMap.get(tecnicoId)!;
      stats.horas_totales += jornada.horas_trabajadas || 0;
      if (jornada.es_hora_extra) {
        stats.horas_extra += jornada.horas_trabajadas || 0;
      }
      stats.jornadas_count += 1;
    });

    // Calculate unique days worked
    statsMap.forEach((stats, tecnicoId) => {
      const uniqueDays = new Set(
        filteredJornadas
          .filter(j => j.tecnico_id === tecnicoId)
          .map(j => j.fecha)
      );
      stats.dias_trabajados = uniqueDays.size;
    });

    return Array.from(statsMap.values()).sort((a, b) => b.horas_totales - a.horas_totales);
  }, [filteredJornadas]);

  // Calendar data
  const calendarDays = useMemo(() => {
    if (calendarView === 'semana') {
      const start = startOfWeek(currentMonth, { weekStartsOn: 1 });
      const end = endOfWeek(currentMonth, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth, calendarView]);

  const getJornadasForDay = (date: Date, tecnicoId?: string) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredJornadas.filter(j => {
      if (j.fecha !== dateStr) return false;
      if (tecnicoId && j.tecnico_id !== tecnicoId) return false;
      return true;
    });
  };

  const getAusenciasForDay = (date: Date) => {
    return allAusencias.filter(a => {
      if (a.estado_validacion === 'rechazada') return false;
      if (selectedTecnico !== 'all' && a.tecnico_id !== selectedTecnico) return false;
      try {
        return isWithinInterval(date, { start: parseISO(a.fecha_inicio), end: parseISO(a.fecha_fin) });
      } catch {
        return false;
      }
    });
  };

  const getOriginLabel = (jornada: Jornada) => {
    if (jornada.obra?.nombre) return { label: jornada.obra.nombre, type: 'obra', color: 'bg-blue-500' };
    if (jornada.orden_trabajo?.numero_ot) return { label: jornada.orden_trabajo.numero_ot, type: 'ot', color: 'bg-green-500' };
    if (jornada.cliente?.nombre) return { label: jornada.cliente.nombre, type: 'cliente', color: 'bg-purple-500' };
    return { label: 'Sin origen', type: 'none', color: 'bg-gray-500' };
  };

  const exportToCSV = () => {
    const headers = ['Fecha', 'Técnico', 'Origen', 'Tipo', 'Inicio', 'Fin', 'Horas', 'Extra', 'Descripción'];
    const rows = filteredJornadas.map(j => [
      format(new Date(j.fecha), 'dd/MM/yyyy'),
      j.tecnico?.nombre || '',
      getOriginLabel(j).label,
      getWorkTypeLabel(j.tipo_trabajo),
      j.hora_inicio,
      j.hora_fin || '',
      j.horas_trabajadas?.toFixed(2) || '',
      j.es_hora_extra ? 'Sí' : 'No',
      j.descripcion_trabajo || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `jornadas_equipo_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Clock className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Jornadas del Equipo</h1>
          <p className="text-muted-foreground">
            Gestión y seguimiento de las jornadas de trabajo del equipo técnico
          </p>
        </div>
        <Button onClick={exportToCSV} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Quick estado filter chips */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm font-medium text-muted-foreground mr-1">Mostrar:</span>
        {([
          { value: 'pendiente', label: 'Pendientes', count: conteosEstado.pendiente, activeClass: 'bg-amber-500 text-white hover:bg-amber-600 border-amber-500' },
          { value: 'aprobada', label: 'Aprobadas', count: conteosEstado.aprobada, activeClass: 'bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600' },
          { value: 'rechazada', label: 'Rechazadas', count: conteosEstado.rechazada, activeClass: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 border-destructive' },
          { value: 'all', label: 'Todas', count: conteosEstado.all, activeClass: 'bg-primary text-primary-foreground hover:bg-primary/90 border-primary' },
        ] as const).map(opt => {
          const active = estadoValidacionFilter === opt.value;
          return (
            <Button
              key={opt.value}
              size="sm"
              variant="outline"
              onClick={() => setEstadoValidacionFilter(opt.value)}
              className={cn("gap-2", active && opt.activeClass)}
            >
              {opt.label}
              <Badge variant={active ? "secondary" : "outline"} className="h-5 px-1.5">
                {opt.count}
              </Badge>
            </Button>
          );
        })}
      </div>


      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <CardTitle className="text-base">Filtros</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-1 block">Técnico</label>
              <Select value={selectedTecnico} onValueChange={setSelectedTecnico}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los técnicos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los técnicos</SelectItem>
                  {tecnicos.map(t => (
                    <SelectItem key={t.user_id} value={t.user_id}>{t.nombre}</SelectItem>
                  ))}
                  {(() => {
                    const knownIds = new Set(tecnicos.map(t => t.user_id));
                    const extras = new Map<string, string>();
                    jornadas.forEach(j => {
                      if (!knownIds.has(j.tecnico_id) && !extras.has(j.tecnico_id)) {
                        extras.set(j.tecnico_id, j.tecnico?.nombre || 'Técnico desconocido');
                      }
                    });
                    return Array.from(extras.entries()).map(([id, nombre]) => (
                      <SelectItem key={id} value={id}>{nombre} (inactivo)</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px]">
              <label className="text-sm font-medium mb-1 block">Desde</label>
              <Input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
              />
            </div>
            <div className="min-w-[150px]">
              <label className="text-sm font-medium mb-1 block">Hasta</label>
              <Input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-sm font-medium mb-1 block">Tipo de trabajo</label>
              <Select value={tipoTrabajoFilter} onValueChange={setTipoTrabajoFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos los tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {Object.entries(tipoTrabajoLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-sm font-medium mb-1 block">Estado validación</label>
              <Select value={estadoValidacionFilter} onValueChange={setEstadoValidacionFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="aprobada">Aprobadas</SelectItem>
                  <SelectItem value="rechazada">Rechazadas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant={soloExtras ? "default" : "outline"}
                onClick={() => setSoloExtras(!soloExtras)}
                className="gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                Solo extras
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {tecnicoStats.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {tecnicoStats.slice(0, 4).map((stats) => (
            <Card key={stats.tecnico_id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium truncate">
                  {stats.nombre}
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.horas_totales.toFixed(1)}h</div>
                <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                  <span>{stats.dias_trabajados} días</span>
                  {stats.horas_extra > 0 && (
                    <Badge variant="destructive" className="text-[10px] h-4 bg-orange-500">
                      +{stats.horas_extra.toFixed(1)}h extra
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Content */}
      <Tabs defaultValue="lista" className="space-y-4">
        <TabsList>
          <TabsTrigger value="lista">Vista Lista</TabsTrigger>
          <TabsTrigger value="calendario">Vista Calendario</TabsTrigger>
          <TabsTrigger value="resumen">Resumen por Técnico</TabsTrigger>
          <TabsTrigger value="ausencias">Ausencias</TabsTrigger>
        </TabsList>

        <TabsContent value="lista">
          <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <p className="text-sm text-muted-foreground">
                {filteredJornadas.length} jornadas encontradas
              </p>
            </div>

            {filteredJornadas.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border border-dashed border-border/60 rounded-lg bg-card/30">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay jornadas que coincidan con los filtros seleccionados.</p>
                <p className="text-sm">Ajusta el período o los filtros para ver resultados.</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {filteredJornadas.slice(0, 50).map((jornada) => {
                    const origin = getOriginLabel(jornada);
                    const accentClass = jornada.es_hora_extra
                      ? 'border-l-orange-500'
                      : origin.type === 'obra'
                      ? 'border-l-blue-500'
                      : origin.type === 'ot'
                      ? 'border-l-emerald-500'
                      : origin.type === 'cliente'
                      ? 'border-l-purple-500'
                      : 'border-l-muted-foreground/40';
                    const OriginIcon = origin.type === 'obra'
                      ? Building2
                      : origin.type === 'ot'
                      ? ClipboardList
                      : Users;

                    return (
                      <Card
                        key={jornada.id}
                        className={`border-l-4 ${accentClass} transition-colors hover:bg-muted/40`}
                      >
                        <CardContent className="p-4 space-y-3">
                          {/* Header: técnico + fecha + tipo + extra */}
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <div className="mt-0.5 shrink-0">
                                <OriginIcon className="w-4 h-4 text-primary" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold truncate">
                                    {jornada.tecnico?.nombre || 'Técnico desconocido'}
                                  </span>
                                  <span className="font-mono text-xs text-muted-foreground">
                                    {format(new Date(jornada.fecha), 'dd/MM/yyyy', { locale: es })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                                  <span className="flex items-center gap-1 truncate max-w-[260px]">
                                    <span className={cn("w-2 h-2 rounded-full shrink-0", origin.color)} />
                                    <span className="truncate">{origin.label}</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge
                                variant="outline"
                                style={{
                                  borderColor: getWorkTypeColor(jornada.tipo_trabajo),
                                  color: getWorkTypeColor(jornada.tipo_trabajo)
                                }}
                                className="text-xs"
                              >
                                {getWorkTypeLabel(jornada.tipo_trabajo)}
                              </Badge>
                              {jornada.es_hora_extra && (
                                <Badge variant="outline" className="text-xs bg-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-500/30">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Hora extra
                                </Badge>
                              )}
                              {jornada.estado_validacion === 'aprobada' && (
                                <Badge variant="outline" className="text-xs bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30">
                                  <Check className="w-3 h-3 mr-1" /> Aprobada
                                </Badge>
                              )}
                              {jornada.estado_validacion === 'rechazada' && (
                                <Badge variant="outline" className="text-xs bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30" title={jornada.motivo_rechazo || ''}>
                                  <X className="w-3 h-3 mr-1" /> Rechazada
                                </Badge>
                              )}
                              {(!jornada.estado_validacion || jornada.estado_validacion === 'pendiente') && (
                                <Badge variant="outline" className="text-xs">Pendiente</Badge>
                              )}
                            </div>
                          </div>

                          {jornada.estado_validacion === 'rechazada' && jornada.motivo_rechazo && (
                            <div className="text-xs rounded-md border border-destructive/40 bg-destructive/10 p-2 text-destructive">
                              <span className="font-medium">Motivo rechazo:</span> {jornada.motivo_rechazo}
                            </div>
                          )}

                          {/* Meta: horario + horas */}
                          <div className="flex items-center gap-x-4 gap-y-1 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {jornada.hora_inicio}
                              {' – '}
                              {jornada.hora_fin || <span className="italic">en curso</span>}
                            </span>
                            <span className="flex items-center gap-1 font-medium text-foreground/80">
                              {jornada.horas_trabajadas?.toFixed(1) || '0'}h
                            </span>
                          </div>

                          {/* Descripción */}
                          {jornada.descripcion_trabajo && (
                            <p className="text-sm line-clamp-2 text-foreground/80">
                              {jornada.descripcion_trabajo}
                            </p>
                          )}

                          {isOfficeRole && jornada.estado_validacion !== 'aprobada' && (
                            <div className="flex flex-wrap gap-2 pt-1 border-t border-border/40">
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-green-700 border-green-500/40 hover:bg-green-500/10"
                                disabled={accionLoading}
                                onClick={() => aprobarJornada(jornada)}
                              >
                                <Check className="w-3.5 h-3.5" /> Aprobar
                              </Button>
                              {jornada.estado_validacion !== 'rechazada' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 text-red-700 border-red-500/40 hover:bg-red-500/10"
                                  disabled={accionLoading}
                                  onClick={() => {
                                    setJornadaARechazar(jornada);
                                    setMotivoRechazo("");
                                    setRechazoDialogOpen(true);
                                  }}
                                >
                                  <X className="w-3.5 h-3.5" /> Rechazar
                                </Button>
                              )}
                              {jornada.estado_validacion === 'rechazada' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1 text-destructive border-destructive/40 hover:bg-destructive/10"
                                  disabled={accionLoading}
                                  onClick={async () => {
                                    if (!window.confirm('¿Eliminar esta jornada rechazada? Esta acción no se puede deshacer.')) return;
                                    try {
                                      await deleteJornada(jornada.id);
                                      setJornadas(prev => prev.filter(j => j.id !== jornada.id));
                                    } catch {
                                      // Error ya mostrado en contexto
                                    }
                                  }}
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                </Button>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                {filteredJornadas.length > 50 && (
                  <p className="text-center text-sm text-muted-foreground mt-2">
                    Mostrando 50 de {filteredJornadas.length} jornadas. Usa los filtros para reducir resultados.
                  </p>
                )}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="calendario">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Calendario del Equipo</CardTitle>
                <CardDescription>
                  {calendarView === 'semana'
                    ? `${format(calendarDays[0], "d MMM", { locale: es })} – ${format(calendarDays[6], "d MMM yyyy", { locale: es })}`
                    : format(currentMonth, 'MMMM yyyy', { locale: es })}
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex rounded-md border overflow-hidden">
                  <Button
                    type="button"
                    variant={calendarView === 'mes' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-none"
                    onClick={() => setCalendarView('mes')}
                  >
                    Mes
                  </Button>
                  <Button
                    type="button"
                    variant={calendarView === 'semana' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-none"
                    onClick={() => setCalendarView('semana')}
                  >
                    Semana
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(prev => {
                    if (calendarView === 'semana') {
                      const d = new Date(prev); d.setDate(d.getDate() - 7); return d;
                    }
                    return new Date(prev.getFullYear(), prev.getMonth() - 1);
                  })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date())}
                >
                  Hoy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonth(prev => {
                    if (calendarView === 'semana') {
                      const d = new Date(prev); d.setDate(d.getDate() + 7); return d;
                    }
                    return new Date(prev.getFullYear(), prev.getMonth() + 1);
                  })}
                >
                  Siguiente
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 mb-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded-full bg-amber-500" /> Con jornadas pendientes
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded-sm ring-2 ring-destructive/40 bg-background" /> Con rechazadas
                </span>
              </div>
              <div className="grid grid-cols-7 gap-1">
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
                {calendarDays.map((day) => {
                  const dayJornadas = getJornadasForDay(day);
                  const dayAusencias = getAusenciasForDay(day);
                  const totalHours = dayJornadas.reduce((sum, j) => sum + (j.horas_trabajadas || 0), 0);
                  const hasExtras = dayJornadas.some(j => j.es_hora_extra);
                  const pendientesCount = dayJornadas.filter(j => j.estado_validacion === 'pendiente').length;
                  const rechazadasCount = dayJornadas.filter(j => j.estado_validacion === 'rechazada').length;
                  const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                  const isWeek = calendarView === 'semana';
                  const ausLimit = isWeek ? dayAusencias.length : 2;
                  
                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        "p-1 border rounded-md relative",
                        isWeek ? "min-h-[220px]" : "min-h-[80px]",
                        isToday(day) && "border-primary",
                        !isWeek && pendientesCount > 0 && "ring-2 ring-amber-400/70 bg-amber-50/40 dark:bg-amber-500/5",
                        !isWeek && pendientesCount === 0 && rechazadasCount > 0 && "ring-2 ring-destructive/40",
                        !isWeek && !isCurrentMonth && "opacity-40"
                      )}
                    >
                      {!isWeek && pendientesCount > 0 && (
                        <span
                          className="absolute -top-1.5 -right-1.5 z-10 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                          title={`${pendientesCount} jornada${pendientesCount > 1 ? 's' : ''} pendiente${pendientesCount > 1 ? 's' : ''} de aprobar`}
                        >
                          {pendientesCount}
                        </span>
                      )}
                      <div className={cn("font-medium mb-1", isWeek ? "text-sm" : "text-xs")}>
                        {isWeek ? format(day, "EEE d", { locale: es }) : format(day, 'd')}
                      </div>
                      {dayAusencias.length > 0 && (
                        <div className="space-y-0.5 mb-1">
                          {dayAusencias.slice(0, ausLimit).map(a => (
                            <div
                              key={a.id}
                              title={`${a.tecnico?.nombre || ''} · ${TIPO_AUSENCIA_LABEL[a.tipo]} · ${a.estado_validacion}`}
                              className={cn(
                                "leading-tight px-1 py-0.5 rounded truncate border-l-2",
                                isWeek ? "text-[10px]" : "text-[9px]",
                                TIPO_AUSENCIA_COLOR[a.tipo],
                                a.estado_validacion === 'aprobada' ? 'border-green-500' : 'border-amber-500'
                              )}
                            >
                              {a.estado_validacion === 'aprobada' ? '✓' : '⏳'} {a.tecnico?.nombre?.split(' ')[0] || ''} {TIPO_AUSENCIA_LABEL[a.tipo]}
                            </div>
                          ))}
                          {!isWeek && dayAusencias.length > 2 && (
                            <div className="text-[9px] text-muted-foreground">+{dayAusencias.length - 2} más</div>
                          )}
                        </div>
                      )}
                      {dayJornadas.length > 0 && (
                        <div
                          className="space-y-0.5 cursor-pointer rounded hover:bg-muted/60 transition-colors focus:outline-none focus:ring-2 focus:ring-ring p-0.5"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirDetalleJornadas(dayJornadas);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              abrirDetalleJornadas(dayJornadas);
                            }
                          }}
                          title="Ver detalle y validar"
                        >
                          <div className={cn("font-semibold text-primary", isWeek ? "text-sm" : "text-xs")}>
                            {totalHours.toFixed(1)}h
                          </div>
                          {!isWeek && (
                            <div className="text-[10px] text-muted-foreground">
                              {dayJornadas.length} jornada{dayJornadas.length > 1 ? 's' : ''}
                            </div>
                          )}
                          {hasExtras && (
                            <Badge variant="destructive" className="text-[8px] h-4 px-1 bg-orange-500">
                              Extra
                            </Badge>
                          )}
                          {isWeek && (
                            <div className="space-y-0.5 mt-1">
                              {dayJornadas.map(j => (
                                <button
                                  key={j.id}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    abrirDetalleJornadas([j]);
                                  }}
                                  className={cn(
                                    "block w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate hover:ring-1 hover:ring-ring transition",
                                    j.estado_validacion === 'aprobada'
                                      ? "bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-200"
                                      : j.estado_validacion === 'rechazada'
                                      ? "bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-200"
                                      : "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-900 dark:text-cyan-200"
                                  )}
                                  title={`${j.tecnico?.nombre || ''} · ${j.horas_trabajadas || 0}h · ${j.estado_validacion}`}
                                >
                                  {j.estado_validacion === 'aprobada' ? '✓ ' : j.estado_validacion === 'rechazada' ? '✕ ' : '⏳ '}
                                  {j.tecnico?.nombre?.split(' ')[0] || ''} · {(j.horas_trabajadas || 0).toFixed(1)}h
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resumen">
          <Card>
            <CardHeader>
              <CardTitle>Resumen por Técnico</CardTitle>
              <CardDescription>
                Estadísticas detalladas del período seleccionado
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tecnicoStats.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No hay datos para el período seleccionado.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Técnico</TableHead>
                      <TableHead className="text-right">Horas Totales</TableHead>
                      <TableHead className="text-right">Horas Extra</TableHead>
                      <TableHead className="text-right">Días Trabajados</TableHead>
                      <TableHead className="text-right">Jornadas</TableHead>
                      <TableHead className="text-right">Media/Día</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tecnicoStats.map((stats) => (
                      <TableRow key={stats.tecnico_id}>
                        <TableCell className="font-medium">{stats.nombre}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {stats.horas_totales.toFixed(1)}h
                        </TableCell>
                        <TableCell className="text-right">
                          {stats.horas_extra > 0 ? (
                            <Badge variant="destructive" className="bg-orange-500">
                              +{stats.horas_extra.toFixed(1)}h
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">0h</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{stats.dias_trabajados}</TableCell>
                        <TableCell className="text-right">{stats.jornadas_count}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {stats.dias_trabajados > 0 
                            ? (stats.horas_totales / stats.dias_trabajados).toFixed(1) + 'h'
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ausencias">
          <AusenciasEquipoTab selectedTecnico={selectedTecnico} fechaDesde={fechaDesde} fechaHasta={fechaHasta} />
        </TabsContent>
      </Tabs>

      <Dialog open={rechazoDialogOpen} onOpenChange={setRechazoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar jornada</DialogTitle>
            <DialogDescription>
              Indica el motivo del rechazo. El técnico verá este mensaje y podrá corregir la jornada.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={motivoRechazo}
            onChange={(e) => setMotivoRechazo(e.target.value)}
            placeholder="Ej: las horas no coinciden con la obra indicada..."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechazoDialogOpen(false)} disabled={accionLoading}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarRechazo} disabled={accionLoading || !motivoRechazo.trim()}>
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <JornadaDetalleCardDialog
        open={detalleOpen}
        onOpenChange={setDetalleOpen}
        jornadas={jornadasDetalle}
        canValidate={isOfficeRole}
        onAprobar={aprobarDesdeDetalle}
        onRechazar={handleRechazarDesdeDetalle}
        onReabrir={isOfficeRole ? reabrirJornada : undefined}
        onEditar={isOfficeRole ? abrirEditorJornada : undefined}
        onEliminar={isOfficeRole ? async (j) => {
          if (!window.confirm('¿Eliminar esta jornada rechazada? Esta acción no se puede deshacer.')) return;
          setAccionLoading(true);
          try {
            await deleteJornada(j.id);
            setJornadas(prev => prev.filter(x => x.id !== j.id));
            setJornadasDetalle(prev => prev.filter(x => x.id !== j.id));
            if (jornadasDetalle.length <= 1) setDetalleOpen(false);
          } catch {
            // Error ya mostrado en contexto
          } finally {
            setAccionLoading(false);
          }
        } : undefined}
        loading={accionLoading}
      />

      <Dialog open={editarJornadaOpen} onOpenChange={(open) => {
        setEditarJornadaOpen(open);
        if (!open) setJornadaEnEdicion(null);
      }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Editar jornada</DialogTitle>
            <DialogDescription>
              Corrige los datos de la jornada. Tras guardar deberás aprobarla de nuevo.
            </DialogDescription>
          </DialogHeader>
          {jornadaEnEdicion && (
            <JornadaForm
              obrasActivas={obrasActivas}
              jornada={jornadaEnEdicion as any}
              onSuccess={() => { recargarTrasEdicion(); }}
              onCancel={() => { setEditarJornadaOpen(false); setJornadaEnEdicion(null); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface AusenciasEquipoTabProps {
  selectedTecnico: string;
  fechaDesde: string;
  fechaHasta: string;
}

const AusenciasEquipoTab: React.FC<AusenciasEquipoTabProps> = ({ selectedTecnico, fechaDesde, fechaHasta }) => {
  const { ausencias, aprobarAusencia, rechazarAusencia, listDocumentos } = useAusencias();
  const [rechazoOpen, setRechazoOpen] = useState<string | null>(null);
  const [motivo, setMotivo] = useState("");

  const filtradas = useMemo(() => {
    return ausencias.filter((a) => {
      if (selectedTecnico !== "all" && a.tecnico_id !== selectedTecnico) return false;
      if (fechaDesde && a.fecha_fin < fechaDesde) return false;
      if (fechaHasta && a.fecha_inicio > fechaHasta) return false;
      return true;
    }).sort((a, b) => b.fecha_inicio.localeCompare(a.fecha_inicio));
  }, [ausencias, selectedTecnico, fechaDesde, fechaHasta]);

  const pendientes = filtradas.filter((a) => a.estado_validacion === "pendiente");
  const aprobadas = filtradas.filter((a) => a.estado_validacion === "aprobada");
  const rechazadas = filtradas.filter((a) => a.estado_validacion === "rechazada");

  const handleAprobar = async (a: typeof filtradas[number]) => {
    if (a.tipo === "baja_medica") {
      const docs = await listDocumentos(a.id);
      if (docs.length === 0) {
        toast({ title: "Falta justificante", description: "Una baja médica requiere documento adjunto", variant: "destructive" });
        return;
      }
    }
    await aprobarAusencia(a.id);
  };

  const handleRechazar = async () => {
    if (!rechazoOpen || !motivo.trim()) return;
    await rechazarAusencia(rechazoOpen, motivo.trim());
    setRechazoOpen(null);
    setMotivo("");
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-amber-500/40">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Pendientes</p>
            <p className="text-2xl font-bold text-amber-600">{pendientes.length}</p>
          </CardContent>
        </Card>
        <Card className="border-green-500/40">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Aprobadas</p>
            <p className="text-2xl font-bold text-green-600">{aprobadas.length}</p>
          </CardContent>
        </Card>
        <Card className="border-red-500/40">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Rechazadas</p>
            <p className="text-2xl font-bold text-red-600">{rechazadas.length}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Link to="/ausencias-equipo">
          <Button variant="outline" size="sm">Gestión completa de ausencias →</Button>
        </Link>
      </div>

      <div className="space-y-2">
        {filtradas.length === 0 && (
          <Card><CardContent className="py-8 text-center text-muted-foreground">No hay ausencias en este período</CardContent></Card>
        )}
        {filtradas.map((a) => (
          <Card key={a.id}>
            <CardContent className="p-3 flex flex-col lg:flex-row lg:items-center gap-2 justify-between">
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <Badge className={TIPO_AUSENCIA_COLOR[a.tipo]}>{TIPO_AUSENCIA_LABEL[a.tipo]}</Badge>
                <span className="font-semibold">{a.tecnico?.nombre || a.tecnico_id.slice(0, 8)}</span>
                <span className="text-sm">
                  {format(parseISO(a.fecha_inicio), "dd/MM/yyyy")} – {format(parseISO(a.fecha_fin), "dd/MM/yyyy")}
                </span>
                <span className="text-xs text-muted-foreground">({Number(a.dias_solicitados)}d)</span>
                {a.estado_validacion === "pendiente" && <Badge className="bg-amber-500 text-white">Pendiente</Badge>}
                {a.estado_validacion === "aprobada" && <Badge className="bg-green-600 text-white">Aprobada</Badge>}
                {a.estado_validacion === "rechazada" && <Badge variant="destructive">Rechazada</Badge>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {a.estado_validacion !== "aprobada" && (
                  <Button size="sm" onClick={() => handleAprobar(a)} className="gap-1">
                    <Check className="h-3.5 w-3.5" /> Aprobar
                  </Button>
                )}
                {a.estado_validacion !== "rechazada" && (
                  <Button size="sm" variant="destructive" onClick={() => { setRechazoOpen(a.id); setMotivo(""); }} className="gap-1">
                    <X className="h-3.5 w-3.5" /> Rechazar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!rechazoOpen} onOpenChange={(v) => { if (!v) setRechazoOpen(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar ausencia</DialogTitle>
          </DialogHeader>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Motivo del rechazo" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechazoOpen(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleRechazar} disabled={!motivo.trim()}>Rechazar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JornadasEquipo;
